/**
 * FASE B — Admin Richieste unificata cross-ramo.
 *
 * Vista tabellare con filtri persistenti in URL, ricerca debounced,
 * sort click su header, paginazione server-side, bulk actions
 * (cambio stato/archivia/soft-delete/export), badge 🔥 cliente ricorrente,
 * modale export con checkbox + motivazione obbligatoria per PII in chiaro.
 */
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import {
  Search, Filter, Download, Eye, Trash2, Archive, RefreshCw, Loader2,
  ChevronDown, ChevronUp, X, AlertTriangle, CheckCircle2, ExternalLink,
  ChevronLeft, ChevronRight, ArrowUpDown, Flame, FileText, LayoutGrid, List,
} from "lucide-react";
import { toast } from "sonner";
import KanbanBoard from "@/components/admin/KanbanBoard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RAMI = [
  "RCA", "D&O", "Cyber", "Fine Arts",
  // FIX Follow-up 1: tutti i rami attivi Famiglia/Viaggi/RC Prof
  "Casa", "Infortuni", "Vita", "Salute", "Viaggi", "RC Professionale",
];
const STATI_UNIFICATI = [
  { key: "nuovo", label: "Nuovo", color: "bg-blue-100 text-blue-800" },
  { key: "in_lavorazione", label: "In lavorazione", color: "bg-amber-100 text-amber-800" },
  { key: "preventivato", label: "Preventivato", color: "bg-purple-100 text-purple-800" },
  { key: "chiuso_positivo", label: "Chiuso ✓", color: "bg-emerald-100 text-emerald-800" },
  { key: "chiuso_negativo", label: "Chiuso ✗", color: "bg-red-100 text-red-800" },
  { key: "archiviato", label: "Archiviato", color: "bg-slate-200 text-slate-700" },
];

const RAMO_BADGE = {
  RCA: "bg-emerald-100 text-emerald-800",
  "D&O": "bg-indigo-100 text-indigo-800",
  Cyber: "bg-fuchsia-100 text-fuchsia-800",
  "Fine Arts": "bg-amber-100 text-amber-800",
  // FIX Follow-up 1: badge per rami Famiglia/Viaggi/RC Prof
  Casa: "bg-teal-100 text-teal-800",
  Infortuni: "bg-orange-100 text-orange-800",
  Vita: "bg-sky-100 text-sky-800",
  Salute: "bg-rose-100 text-rose-800",
  Viaggi: "bg-cyan-100 text-cyan-800",
  "RC Professionale": "bg-violet-100 text-violet-800",
};

// FIX BUG "Convenzione persa" — Filtro canale/partner unificato cross-ramo.
const PARTNERS = [
  { id: "stellantis", label: "Stellantis" },
  { id: "leonardo", label: "Leonardo" },
  { id: "gori", label: "GORI" },
  { id: "unicredit", label: "CRAL Unicredit" },
  { id: "ge_avio", label: "GE Avio" },
  { id: "hitachi", label: "Hitachi Rail" },
];

function useDebouncedValue(v, delay = 300) {
  const [dv, setDv] = useState(v);
  useEffect(() => {
    const t = setTimeout(() => setDv(v), delay);
    return () => clearTimeout(t);
  }, [v, delay]);
  return dv;
}

function StatusBadge({ tech, unified }) {
  const s = STATI_UNIFICATI.find((x) => x.key === unified) || STATI_UNIFICATI[0];
  return (
    <span
      title={`Status tecnico: ${tech || "—"}`}
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${s.color}`}
      data-testid={`status-badge-${unified}`}
    >
      {s.label}
    </span>
  );
}

function RamoBadge({ ramo }) {
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium ${RAMO_BADGE[ramo] || "bg-slate-100 text-slate-700"}`}>
      {ramo}
    </span>
  );
}

function ExportModal({ open, onClose, onExport, filterSummary, selectedCount }) {
  const [includePii, setIncludePii] = useState(false);
  const [consentPii, setConsentPii] = useState(false);
  const [motiv, setMotiv] = useState("");
  const [submitting, setSubmitting] = useState(false);
  if (!open) return null;
  const canSubmit = !includePii || (consentPii && motiv.trim().length >= 10);
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
          onClick={onClose} data-testid="export-modal">
      <div className="bg-white rounded-2xl max-w-lg w-full p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="font-heading text-xl text-[#0F1F14]">Esporta richieste</h3>
            <p className="text-xs text-[#4A5E51] mt-1">
              {selectedCount > 0
                ? `${selectedCount} righe selezionate`
                : `Filtro corrente: ${filterSummary}`}
            </p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded">
            <X className="w-5 h-5" />
          </button>
        </div>
        <label className="flex items-start gap-3 mb-3 cursor-pointer" data-testid="export-toggle-pii">
          <input type="checkbox" checked={includePii}
                  onChange={(e) => setIncludePii(e.target.checked)}
                  className="mt-1 w-4 h-4 accent-[#163321]" />
          <span className="text-sm">
            <strong>Includi PII in chiaro</strong> (codice fiscale, telefono, email non mascherati)
          </span>
        </label>
        {includePii && (
          <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 mb-3">
            <div className="flex items-start gap-2 mb-2">
              <AlertTriangle className="w-4 h-4 text-amber-700 mt-0.5" />
              <p className="text-xs text-amber-900">
                L&apos;export con dati personali in chiaro verrà tracciato
                nell&apos;audit log admin (chi, quando, filtro, motivazione, SHA256).
              </p>
            </div>
            <label className="flex items-start gap-2 mb-3 cursor-pointer"
                    data-testid="export-consent-checkbox">
              <input type="checkbox" checked={consentPii}
                      onChange={(e) => setConsentPii(e.target.checked)}
                      className="mt-0.5 w-4 h-4 accent-[#163321]" />
              <span className="text-xs">
                Confermo di voler esportare dati personali (PII) in chiaro.
              </span>
            </label>
            <label className="block text-xs mb-1">Motivazione obbligatoria (min 10 caratteri)</label>
            <textarea rows={3}
                       value={motiv}
                       onChange={(e) => setMotiv(e.target.value)}
                       placeholder="Es. Trasferimento dati per obbligo IVASS art. 40..."
                       data-testid="export-motivazione-input"
                       className="w-full text-sm px-3 py-2 border rounded-lg" />
            <p className="text-[10px] text-slate-500 mt-1">
              {motiv.trim().length} / 10 caratteri
            </p>
          </div>
        )}
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose}
                   className="px-4 py-2 rounded-lg text-sm hover:bg-slate-100">
            Annulla
          </button>
          <button
            disabled={!canSubmit || submitting}
            onClick={async () => {
              setSubmitting(true);
              try {
                await onExport({ include_pii: includePii,
                                    pii_consent_checkbox: consentPii,
                                    pii_motivazione: motiv });
                onClose();
              } finally {
                setSubmitting(false);
              }
            }}
            data-testid="export-submit-btn"
            className={`px-4 py-2 rounded-lg text-sm font-medium inline-flex items-center gap-2 ${
              (!canSubmit || submitting)
                ? "bg-slate-300 text-slate-500 cursor-not-allowed"
                : "bg-[#163321] text-white hover:bg-[#0F1F14]"
            }`}>
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            Scarica CSV
          </button>
        </div>
      </div>
    </div>
  );
}

function ConfirmDeleteModal({ open, onClose, onConfirm, count }) {
  const [txt, setTxt] = useState("");
  useEffect(() => { if (!open) setTxt(""); }, [open]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
          onClick={onClose} data-testid="confirm-delete-modal">
      <div className="bg-white rounded-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-heading text-xl text-red-800 mb-2">Elimina {count} richieste</h3>
        <p className="text-sm text-slate-700 mb-3">
          Soft-delete: le richieste saranno nascoste dalla vista ma resteranno in DB.
          Per confermare, digita <strong>ELIMINA</strong> qui sotto:
        </p>
        <input value={txt} onChange={(e) => setTxt(e.target.value)}
                data-testid="confirm-delete-input"
                className="w-full border rounded-lg px-3 py-2 text-sm mb-4" />
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-2 rounded-lg text-sm hover:bg-slate-100">Annulla</button>
          <button
            disabled={txt !== "ELIMINA"}
            onClick={() => { onConfirm(); onClose(); }}
            data-testid="confirm-delete-confirm-btn"
            className={`px-4 py-2 rounded-lg text-sm font-medium ${
              txt !== "ELIMINA" ? "bg-slate-300 text-slate-500 cursor-not-allowed"
                                    : "bg-red-600 text-white hover:bg-red-700"}`}>
            Elimina definitivamente
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminRichiestePage() {
  const [sp, setSp] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState({});
  const [showExport, setShowExport] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [showBulkStatus, setShowBulkStatus] = useState(false);
  const [bulkTargetStatus, setBulkTargetStatus] = useState("in_lavorazione");
  const [tasksCount, setTasksCount] = useState(0);
  const [tasksModal, setTasksModal] = useState(false);
  const [tasksList, setTasksList] = useState([]);
  // FASE G — vista Tabella/Kanban (persistente in URL)
  const view = (sp.get("view") === "kanban") ? "kanban" : "table";
  const setView = useCallback((v) => {
    const next = new URLSearchParams(sp);
    if (v === "kanban") next.set("view", "kanban");
    else next.delete("view");
    setSp(next);
  }, [sp, setSp]);

  const filters = useMemo(() => ({
    ramo: sp.get("ramo") || "",
    status: sp.get("status") || "",
    partner: sp.get("partner") || "",
    q: sp.get("q") || "",
    date_from: sp.get("date_from") || "",
    date_to: sp.get("date_to") || "",
    sort_by: sp.get("sort_by") || "created_at",
    sort_dir: sp.get("sort_dir") || "desc",
    page: parseInt(sp.get("page") || "1", 10),
    page_size: parseInt(sp.get("page_size") || "25", 10),
  }), [sp]);

  const qDebounced = useDebouncedValue(filters.q, 300);

  const setFilter = useCallback((key, value) => {
    const next = new URLSearchParams(sp);
    if (value === "" || value == null) next.delete(key);
    else next.set(key, String(value));
    if (key !== "page") next.set("page", "1");
    setSp(next);
  }, [sp, setSp]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries({ ...filters, q: qDebounced }).forEach(([k, v]) => {
        if (v !== "" && v != null) params.set(k, String(v));
      });
      const r = await axios.get(`${API}/admin/richieste?${params.toString()}`,
                                    { withCredentials: true });
      setRows(r.data.items || []);
      setTotal(r.data.total || 0);
    } catch (e) {
      toast.error("Errore caricamento richieste");
    } finally {
      setLoading(false);
    }
  }, [filters, qDebounced]);

  const loadTasksCount = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/crm-tasks?status=aperto,in_lavorazione&limit=1`,
                                    { withCredentials: true });
      setTasksCount(r.data.total || 0);
    } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadTasksCount(); }, [loadTasksCount]);

  const selectedList = useMemo(
    () => rows.filter((r) => selectedIds[`${r.module}|${r.id}`]),
    [rows, selectedIds]);
  const allSelected = rows.length > 0 && rows.every((r) => selectedIds[`${r.module}|${r.id}`]);

  const toggleSort = (col) => {
    if (filters.sort_by === col) {
      setFilter("sort_dir", filters.sort_dir === "desc" ? "asc" : "desc");
    } else {
      setFilter("sort_by", col);
      setFilter("sort_dir", "desc");
    }
  };

  const doBulkAction = async (action, targetStatus = null) => {
    if (selectedList.length === 0) return;
    try {
      await axios.post(`${API}/admin/richieste/bulk-action`, {
        action,
        target_status: targetStatus,
        ids: selectedList.map((r) => ({ module: r.module, id: r.id })),
      }, { withCredentials: true });
      toast.success(`${action} applicato a ${selectedList.length} righe`);
      setSelectedIds({});
      load();
    } catch (e) {
      toast.error("Errore bulk action");
    }
  };

  const doExport = async ({ include_pii, pii_consent_checkbox, pii_motivazione }) => {
    const payload = {
      ramo: filters.ramo || null, status: filters.status || null,
      partner: filters.partner || null, q: qDebounced || null,
      date_from: filters.date_from || null, date_to: filters.date_to || null,
      format: "csv",
      include_pii, pii_consent_checkbox, pii_motivazione,
      selected_ids: selectedList.length
        ? selectedList.map((r) => ({ module: r.module, id: r.id }))
        : null,
    };
    const r = await axios.post(`${API}/admin/richieste/export`, payload, {
      withCredentials: true, responseType: "blob",
    });
    const blob = new Blob([r.data], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `richieste_colucci_${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Esportato ${r.headers["x-export-rows"] || "?"} righe`);
  };

  const openTasksModal = async () => {
    try {
      const r = await axios.get(`${API}/admin/crm-tasks?status=aperto,in_lavorazione&limit=50`,
                                     { withCredentials: true });
      setTasksList(r.data.items || []);
      setTasksModal(true);
    } catch (e) {
      toast.error("Errore caricamento task");
    }
  };

  const filterSummary = [
    filters.ramo && `Ramo: ${filters.ramo}`,
    filters.status && `Stato: ${filters.status}`,
    filters.partner && `Partner: ${filters.partner}`,
    qDebounced && `Cerca: "${qDebounced}"`,
    filters.date_from && `Da: ${filters.date_from}`,
    filters.date_to && `A: ${filters.date_to}`,
  ].filter(Boolean).join(" · ") || "tutte le richieste";

  const totPages = Math.max(1, Math.ceil(total / filters.page_size));

  return (
    <div className="p-6 max-w-full" data-testid="admin-richieste-page">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4 mb-5">
        <div>
          <h1 className="font-heading text-3xl text-[#0F1F14]">Richieste</h1>
          <p className="text-sm text-[#4A5E51] mt-1">
            Vista unica cross-ramo · {total} richieste totali
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* Toggle Tabella / Kanban */}
          <div className="inline-flex bg-slate-100 rounded-lg p-0.5"
                data-testid="view-toggle">
            <button
              data-testid="view-toggle-table"
              onClick={() => setView("table")}
              className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-medium transition
                          ${view === "table" ? "bg-white text-[#0F1F14] shadow-sm" : "text-slate-600 hover:text-[#0F1F14]"}`}>
              <List className="w-3.5 h-3.5" /> Tabella
            </button>
            <button
              data-testid="view-toggle-kanban"
              onClick={() => setView("kanban")}
              className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-medium transition
                          ${view === "kanban" ? "bg-white text-[#0F1F14] shadow-sm" : "text-slate-600 hover:text-[#0F1F14]"}`}>
              <LayoutGrid className="w-3.5 h-3.5" /> Kanban
            </button>
          </div>
          <button onClick={openTasksModal}
                   data-testid="tasks-widget"
                   className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium ${
                     tasksCount > 0
                       ? "bg-red-100 text-red-800 hover:bg-red-200 animate-pulse"
                       : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
            <Flame className="w-4 h-4" />
            Task aperti: {tasksCount}
          </button>
          <button onClick={() => load()}
                   data-testid="refresh-btn"
                   className="p-2 rounded-lg hover:bg-slate-100">
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {/* Filtri */}
      <div className="bg-white border border-[#E5E2DB] rounded-2xl p-4 mb-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3">
          <div className="lg:col-span-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                data-testid="richieste-search-input"
                placeholder="Cerca nome, CF, targa, email..."
                value={filters.q}
                onChange={(e) => setFilter("q", e.target.value)}
                className="w-full pl-9 pr-3 py-2 text-sm border rounded-lg" />
            </div>
          </div>
          <select data-testid="filter-ramo"
                   value={filters.ramo}
                   onChange={(e) => setFilter("ramo", e.target.value)}
                   className="px-3 py-2 text-sm border rounded-lg">
            <option value="">Tutti i rami</option>
            {RAMI.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
          <select data-testid="filter-status"
                   value={filters.status}
                   onChange={(e) => setFilter("status", e.target.value)}
                   className="px-3 py-2 text-sm border rounded-lg">
            <option value="">Tutti gli stati</option>
            {STATI_UNIFICATI.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
          {/* FIX BUG "Convenzione persa" — filtro canale unificato cross-ramo */}
          <select data-testid="filter-partner"
                   value={filters.partner}
                   onChange={(e) => setFilter("partner", e.target.value)}
                   className="px-3 py-2 text-sm border rounded-lg"
                   title="Filtro convenzione/canale">
            <option value="">Tutti i canali</option>
            <option value="__diretto__">Diretto (senza convenzione)</option>
            {PARTNERS.map((p) => (
              <option key={p.id} value={p.id}>Conv. {p.label}</option>
            ))}
          </select>
          <input type="date" value={filters.date_from}
                  data-testid="filter-date-from"
                  onChange={(e) => setFilter("date_from", e.target.value)}
                  className="px-3 py-2 text-sm border rounded-lg"
                  placeholder="Da" />
          <input type="date" value={filters.date_to}
                  data-testid="filter-date-to"
                  onChange={(e) => setFilter("date_to", e.target.value)}
                  className="px-3 py-2 text-sm border rounded-lg"
                  placeholder="A" />
        </div>
      </div>

      {/* Bulk toolbar */}
      {selectedList.length > 0 && (
        <div className="bg-[#163321] text-white rounded-lg p-3 mb-3 flex flex-wrap items-center gap-3"
              data-testid="bulk-toolbar">
          <span className="text-sm font-medium">{selectedList.length} selezionate</span>
          <button onClick={() => setShowBulkStatus(true)}
                   data-testid="bulk-status-btn"
                   className="text-xs bg-white/10 hover:bg-white/20 px-3 py-1.5 rounded">
            Cambia stato
          </button>
          <button onClick={() => doBulkAction("archive")}
                   data-testid="bulk-archive-btn"
                   className="text-xs bg-white/10 hover:bg-white/20 px-3 py-1.5 rounded inline-flex items-center gap-1">
            <Archive className="w-3 h-3" /> Archivia
          </button>
          <button onClick={() => setShowDelete(true)}
                   data-testid="bulk-delete-btn"
                   className="text-xs bg-red-500/80 hover:bg-red-500 px-3 py-1.5 rounded inline-flex items-center gap-1">
            <Trash2 className="w-3 h-3" /> Elimina
          </button>
          <button onClick={() => setShowExport(true)}
                   data-testid="bulk-export-btn"
                   className="text-xs bg-white/10 hover:bg-white/20 px-3 py-1.5 rounded inline-flex items-center gap-1">
            <Download className="w-3 h-3" /> Export selezione
          </button>
          <button onClick={() => setSelectedIds({})}
                   className="text-xs ml-auto hover:underline">Deseleziona</button>
        </div>
      )}

      {/* Toolbar top */}
      <div className="flex justify-between items-center mb-3">
        <div className="text-xs text-[#4A5E51]">
          {loading ? "Caricamento…" : `Mostrando ${rows.length} di ${total}`}
        </div>
        <button onClick={() => setShowExport(true)}
                 data-testid="global-export-btn"
                 className="text-xs bg-slate-100 hover:bg-slate-200 px-3 py-1.5 rounded-lg inline-flex items-center gap-1">
          <Download className="w-3 h-3" /> Esporta filtro
        </button>
      </div>

      {/* Tabella / Kanban switch */}
      {view === "kanban" ? (
        <div className="bg-white border border-[#E5E2DB] rounded-2xl p-3">
          <KanbanBoard
            rows={rows}
            onView={(r) => window.open(
              r.module === "d_o" ? `/admin/dashboard#richieste_do_${r.id}` :
              r.module === "cyber" ? `/admin/dashboard#richieste_cyber_${r.id}` :
              r.module === "fine_arts" ? `/admin/dashboard#richieste_fa_${r.id}` :
              `/admin/dashboard#convenzioni_${r.id}`,
              "_blank"
            )}
            onAfterMove={(rowId, newStatus) => {
              setRows((cur) => cur.map((r) =>
                r.id === rowId ? { ...r, status_unified: newStatus } : r
              ));
            }}
          />
        </div>
      ) : (
      <div className="bg-white border border-[#E5E2DB] rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm" data-testid="richieste-table">
            <thead className="bg-slate-50 text-left">
              <tr>
                <th className="p-3 w-8">
                  <input type="checkbox"
                          checked={allSelected}
                          data-testid="select-all-checkbox"
                          onChange={(e) => {
                            if (e.target.checked) {
                              const s = {};
                              rows.forEach((r) => { s[`${r.module}|${r.id}`] = true; });
                              setSelectedIds(s);
                            } else {
                              setSelectedIds({});
                            }
                          }} />
                </th>
                <th onClick={() => toggleSort("created_at")}
                    className="p-3 cursor-pointer whitespace-nowrap">Data <ArrowUpDown className="inline w-3 h-3" /></th>
                <th onClick={() => toggleSort("ramo")}
                    className="p-3 cursor-pointer">Ramo</th>
                <th className="p-3">Cliente</th>
                <th className="p-3">CF</th>
                <th className="p-3">Tel</th>
                <th className="p-3">Compagnia/Partner</th>
                <th onClick={() => toggleSort("status")}
                    className="p-3 cursor-pointer">Stato</th>
                <th onClick={() => toggleSort("num_richieste")}
                    className="p-3 cursor-pointer whitespace-nowrap">Richieste cliente</th>
                <th className="p-3">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const key = `${r.module}|${r.id}`;
                const isHot = (r.num_richieste_cliente >= 3);
                return (
                  <tr key={key}
                       className="border-t border-slate-100 hover:bg-slate-50"
                       data-testid={`richiesta-row-${r.id}`}>
                    <td className="p-3">
                      <input type="checkbox"
                              checked={!!selectedIds[key]}
                              onChange={(e) => setSelectedIds({
                                ...selectedIds,
                                [key]: e.target.checked,
                              })} />
                    </td>
                    <td className="p-3 text-xs whitespace-nowrap">
                      {r.created_at ? new Date(r.created_at).toLocaleString("it-IT",
                        { day: "2-digit", month: "2-digit", year: "2-digit",
                          hour: "2-digit", minute: "2-digit" }) : "—"}
                    </td>
                    <td className="p-3"><RamoBadge ramo={r.ramo} /></td>
                    <td className="p-3">
                      <div className="font-medium text-[13px]">
                        {[r.nome, r.cognome].filter(Boolean).join(" ") || "—"}
                      </div>
                      <div className="text-[10px] text-slate-500">#{r.numero_pratica}</div>
                    </td>
                    <td className="p-3 text-xs font-mono">{r.cf_masked || "—"}</td>
                    <td className="p-3 text-xs">{r.tel_masked || "—"}</td>
                    <td className="p-3 text-xs">
                      {r.compagnia || r.partner || "—"}
                    </td>
                    <td className="p-3"><StatusBadge tech={r.status_tech} unified={r.status_unified} /></td>
                    <td className="p-3">
                      {isHot ? (
                        <span
                          title="Cliente ad alto potenziale, non ancora convertito. Follow-up prioritario."
                          className="inline-flex items-center gap-1 bg-red-600 text-white px-2 py-0.5 rounded-full text-[10px] font-bold animate-pulse"
                          data-testid={`hot-badge-${r.id}`}>
                          <Flame className="w-3 h-3" /> {r.num_richieste_cliente} richieste
                        </span>
                      ) : (
                        <span className="text-xs text-slate-500">{r.num_richieste_cliente}</span>
                      )}
                    </td>
                    <td className="p-3">
                      <div className="flex items-center gap-1">
                        {r.cliente_unificato_id && (
                          <Link
                            to={`/admin/clienti/${r.cliente_unificato_id}`}
                            data-testid={`view-cliente-${r.id}`}
                            className="p-1.5 rounded hover:bg-slate-200"
                            title="Vedi cliente unificato">
                            <Eye className="w-4 h-4" />
                          </Link>
                        )}
                        {r.has_pdf && (
                          <a
                            href={`${API.replace(/\/api$/, "")}/api/admin/${r.module}/pdf/${r.id}`}
                            target="_blank" rel="noreferrer"
                            className="p-1.5 rounded hover:bg-slate-200"
                            title="Scarica PDF">
                            <Download className="w-4 h-4" />
                          </a>
                        )}
                        {(r.module === "d_o" || r.module === "cyber" || r.module === "fine_arts") && (
                          <a
                            href={
                              r.module === "d_o"
                                ? `${API}/admin/do/questionnaires/${r.id}/pdf-unified-admin`
                                : r.module === "cyber"
                                ? `${API}/admin/cyber/pratiche/${r.id}/pdf-unified-admin`
                                : `${API}/admin/fine-arts/pratiche/${r.id}/pdf-unified-admin`
                            }
                            target="_blank" rel="noreferrer"
                            data-testid={`pdf-admin-btn-${r.id}`}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded bg-[#163321] text-white text-[10px] font-semibold hover:bg-[#1F3A28]"
                            title="Scarica PDF Unificato ADMIN (arricchito · metadata IVASS + timeline + integrità)">
                            <FileText className="w-3 h-3" /> PDF Admin
                          </a>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {rows.length === 0 && !loading && (
                <tr>
                  <td colSpan={10} className="p-8 text-center text-slate-500">
                    Nessuna richiesta trovata con i filtri attuali.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      )}

      {/* Paginazione (solo vista tabella) */}
      {view === "table" && (
      <div className="flex items-center justify-between mt-4">
        <div className="text-xs text-slate-600">Pagina {filters.page} di {totPages}</div>
        <div className="flex items-center gap-2">
          <select value={filters.page_size}
                   onChange={(e) => setFilter("page_size", e.target.value)}
                   data-testid="page-size-select"
                   className="text-xs border rounded px-2 py-1">
            {[25, 50, 100].map((n) => <option key={n} value={n}>{n}/pag</option>)}
          </select>
          <button
            disabled={filters.page <= 1}
            data-testid="prev-page-btn"
            onClick={() => setFilter("page", filters.page - 1)}
            className="p-1.5 rounded border disabled:opacity-30 hover:bg-slate-100">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button
            disabled={filters.page >= totPages}
            data-testid="next-page-btn"
            onClick={() => setFilter("page", filters.page + 1)}
            className="p-1.5 rounded border disabled:opacity-30 hover:bg-slate-100">
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
      )}

      {/* Modali */}
      <ExportModal
        open={showExport}
        onClose={() => setShowExport(false)}
        onExport={doExport}
        filterSummary={filterSummary}
        selectedCount={selectedList.length}
      />
      <ConfirmDeleteModal
        open={showDelete} onClose={() => setShowDelete(false)}
        count={selectedList.length}
        onConfirm={() => doBulkAction("soft_delete")}
      />
      {showBulkStatus && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
              onClick={() => setShowBulkStatus(false)}
              data-testid="bulk-status-modal">
          <div className="bg-white rounded-2xl max-w-md w-full p-6"
                onClick={(e) => e.stopPropagation()}>
            <h3 className="font-heading text-lg mb-3">Cambia stato per {selectedList.length} richieste</h3>
            <select value={bulkTargetStatus}
                     onChange={(e) => setBulkTargetStatus(e.target.value)}
                     data-testid="bulk-status-target"
                     className="w-full px-3 py-2 border rounded-lg mb-4">
              {STATI_UNIFICATI.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
            <div className="flex justify-end gap-2">
              <button onClick={() => setShowBulkStatus(false)}
                       className="px-3 py-2 text-sm hover:bg-slate-100 rounded">Annulla</button>
              <button
                data-testid="bulk-status-apply"
                onClick={() => { doBulkAction("update_status", bulkTargetStatus); setShowBulkStatus(false); }}
                className="px-4 py-2 bg-[#163321] text-white text-sm rounded font-medium">
                Applica
              </button>
            </div>
          </div>
        </div>
      )}
      {tasksModal && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
              onClick={() => setTasksModal(false)}
              data-testid="tasks-modal">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[85vh] p-6 overflow-y-auto"
                onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-xl">Task follow-up alto potenziale</h3>
              <button onClick={() => setTasksModal(false)} className="p-1"><X className="w-5 h-5" /></button>
            </div>
            {tasksList.length === 0 && <p className="text-sm text-slate-600">Nessun task aperto.</p>}
            <div className="space-y-2">
              {tasksList.map((t) => (
                <Link key={t.id}
                       to={`/admin/clienti/${t.cliente_unificato_id}`}
                       className="block border rounded-lg p-3 hover:bg-slate-50">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium">
                        {t.cliente_snapshot?.nome_primario} {t.cliente_snapshot?.cognome_primario}
                      </div>
                      <div className="text-[11px] text-slate-500">
                        {t.cliente_snapshot?.num_richieste_totali} richieste · trigger: {t.trigger_reason}
                      </div>
                    </div>
                    <span className="text-[10px] uppercase tracking-wide bg-red-100 text-red-800 px-2 py-0.5 rounded">
                      {t.priorita}
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
